import React, { useEffect, useState } from "react";
import "../../tailwind.css";
import { IEvidenceDisplay } from "../../types/EvidenceDisplay.types";
import {
  EvidenceAttachment,
  EvidencePart,
  EvidenceStandardTag,
  FieldType,
  LearningObjective,
} from "../../types/Interfaces";
import { format } from "date-fns";
import DOMPurify from "dompurify";
import { StringUtils } from "../../helpers/stringUtils";
import { IonSpinner } from "@ionic/react";
import { FileUtils } from "../../helpers/fileUtils";
import { EvidenceHelpers } from "../../helpers/EvidenceHelper";
import { CreateEvidencePart } from "../../parts/evidenceParts/ComponentWrapper";
import { ProgramUtils } from "../../helpers/programUtils";
import EvidenceSkillDisplay from "./EvidenceSkillDisplay";
import LegacyEvidenceFields from "./LegacyEvidenceFields";
import { autoLearningOutcomeLegacyKeys } from "../../constants/evidencePartsLookup";
import { useLegacyEvidenceRenderer } from "../LegacyEvidenceRenderer";
import { flattenEvidenceParts } from "../../helpers/evidenceUtils";

const EvidenceDisplay: React.FC<IEvidenceDisplay> = (props) => {
  const [evidenceParts, setEvidenceParts] = useState<EvidencePart[]>([]);

  const [evidenceJSON, setEvidenceJSON] = useState<any>(null);

  const [viewingAttachment, setViewingAttachment] = useState<boolean>(false);
  const [viewingAttachmentFilename, setViewingAttachmentFilename] = useState<string>("");

  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (props.program && props.evidence?.evidenceJSON) {
      const evidenceParts = EvidenceHelpers.getEvidenceParts({
        program: props.program,
        evidence: props.evidence,
        user: props.user,
      });

      const flattenedEvidenceParts = flattenEvidenceParts(evidenceParts);

      setEvidenceParts(flattenedEvidenceParts);
      if (props.evidence.evidenceJSON) {
        if (typeof props.evidence.evidenceJSON === "string") {
          setEvidenceJSON(JSON.parse(props.evidence.evidenceJSON));
        } else {
          setEvidenceJSON(props.evidence.evidenceJSON);
        }
      }
    }

    setLoading(false);
  }, [props.evidence]);

  const getLearningOutcomesForEvidencePart = (): LearningObjective[] => {
    if (props.program && ProgramUtils.getLearningOutcomesFromRole(props.program.ID)) {
      return ProgramUtils.getAllLearningOutcomesFromRoles(props.program);
    }

    if (props.progressCheckCompetence) {
      return props.progressCheckCompetence["Learning Objective"] ?? [];
    }

    return [];
  };

  const sanitizeData = (data: string) => ({ __html: DOMPurify.sanitize(data) });

  const viewAttachment = async (attachment: EvidenceAttachment): Promise<void> => {
    try {
      if (attachment.url && props.contactID && props.token) {
        setViewingAttachment(true);
        setViewingAttachmentFilename(attachment.name);

        await FileUtils.viewAttachment(
          props.token,
          props.contactID,
          props.evidenceContactID,
          props.evidence?.id ?? "",
          attachment
        );

        setViewingAttachment(false);
        setViewingAttachmentFilename("");
      }
    } catch (error) {
      console.log(error);
      setViewingAttachment(false);
      setViewingAttachmentFilename("");
      window.alert("An error occurred when opening attachment");
    }
  };

  if (loading) {
    return null;
  }

  const { getLegacyEvidencePartType, renderLegacyEvidencePart } = useLegacyEvidenceRenderer({
    viewingAttachment,
    viewingAttachmentFilename,
    onViewAttachment: viewAttachment,
  });

  const witnessSimulationSkillID = evidenceJSON?.programInfo?.witnessSimulationSkillID;

  const getLegacyEvidenceParts = (): string[] => {
    if (!evidenceJSON) {
      return [];
    }

    const hasAutomaticLearningOutcomes = props.progressCheckCompetence?.["LOs Automatic"] ?? false;

    const copy = structuredClone(evidenceJSON);
    delete copy.programInfo;

    const keys = Object.keys(copy);

    const hasNOSSelectPart = evidenceParts.some((part: EvidencePart) => part["Field type"] === FieldType.NOSSelect);

    const allEvidenceParts = flattenEvidenceParts(evidenceParts);

    const keysWithoutEvidencePart = keys
      .filter((key) => {
        const evidencePart = allEvidenceParts.find((part: EvidencePart) => part.Name === key);

        if (evidencePart) return false;

        if (key === "Title") return false;

        if ((key === "LOs" || key === "Comps") && hasNOSSelectPart) return false;

        if (hasAutomaticLearningOutcomes && autoLearningOutcomeLegacyKeys.has(key)) return false;

        return true;
      })
      .filter((key) => key !== "OnHoldReset");

    return keysWithoutEvidencePart;
  };

  // const legacyParts = useMemo(() => getLegacyEvidenceParts(), [evidenceJSON, evidenceParts]);

  const legacyParts = getLegacyEvidenceParts();

  return (
    <div>
      {props.evidence ? (
        <>
          {evidenceJSON ? (
            <>
              {evidenceJSON.Title && (
                <div>
                  <div className="text-[28px] font-bold leading-[1.21] tracking-[-0.4px] text-grey-90">
                    {evidenceJSON.Title}
                  </div>
                  <div className="h-[1px] w-full bg-grey-30 mt-4" />
                </div>
              )}
              {props.evidence.date && (
                <div className="mt-[20px]">
                  <div className="text-acc-13px font-bold leading-default tracking-[-0.1px] text-grey-90 mb-1">
                    {"Date"}
                  </div>
                  <div className="text-acc-17px font-acc-normal leading-default tracking-[-0.3px] text-grey-70">
                    {format(new Date(props.evidence.date), "d MMM yyyy")}
                  </div>
                  <div className="h-[1px] w-full bg-grey-30 mt-4" />
                </div>
              )}
              <EvidenceSkillDisplay skillID={witnessSimulationSkillID} program={props.program} />
              {evidenceParts.map((item: EvidencePart, index: number) => {
                let dataForPart;
                let mandatoryStandards;
                let optionalStandards;

                if (item["Field type"] === FieldType.NOSSelect) {
                  dataForPart = {
                    Comps: evidenceJSON.Comps,
                    LOs: evidenceJSON.LOs,
                  };

                  mandatoryStandards = EvidenceHelpers.getMandatoryStandards(props.user, props.program);
                  optionalStandards = EvidenceHelpers.getOptionalStandards(props.user, props.program);
                } else {
                  dataForPart = evidenceJSON[item.Name];
                }

                if (dataForPart) {
                  return (
                    <div className="mt-[20px]" key={`${item.Name}-${index}`}>
                      {CreateEvidencePart(item["Field type"], {
                        definition: item,
                        data: dataForPart,
                        evidenceID: props.evidence?.id,
                        contactID: props.contactID,
                        evidenceContactID: props.evidenceContactID,
                        progressCheckCompetence: props.progressCheckCompetence,
                        learningOutcomes: getLearningOutcomesForEvidencePart(),
                        token: props.token,
                        user: props.user,
                        mandatoryStandards,
                        optionalStandards,
                        isDisplay: true,
                      })}
                    </div>
                  );
                } else {
                  return null;
                }
              })}
              <div className="mt-[20px]">
                <div className="text-acc-13px font-bold leading-default tracking-[-0.1px] text-grey-90 mb-[4px]">
                  {"Included in audit documents"}
                </div>
                <div className="text-acc-17px font-acc-normal leading-default tracking-[-0.3px] text-grey-70">
                  {props.evidence.addToHCPCAudit ? "Yes" : "No"}
                </div>
              </div>
            </>
          ) : (
            <>
              {props.evidence.title && (
                <div>
                  <div className="text-[28px] font-bold leading-[1.21] tracking-[-0.4px] text-grey-90">
                    {props.evidence.title}
                  </div>
                  <div className="h-[1px] w-full bg-grey-30 mt-4" />
                </div>
              )}
              {props.evidence.date && (
                <div className="mt-[20px]">
                  <div className="text-acc-13px font-bold leading-default tracking-[-0.1px] text-grey-90 mb-[4px]">
                    {"Date of reflection"}
                  </div>
                  <div className="text-acc-17px font-acc-normal leading-default tracking-[-0.3px] text-grey-70">
                    {format(new Date(props.evidence.date), "d MMM yyyy")}
                  </div>
                  <div className="h-[1px] w-full bg-grey-30 mt-4" />
                </div>
              )}
              {props.evidence.activityDate && (
                <div className="mt-[20px]">
                  <div className="text-acc-13px font-bold leading-default tracking-[-0.1px] text-grey-90 mb-[4px]">
                    {"Date of activity"}
                  </div>
                  <div className="text-acc-17px font-acc-normal leading-default tracking-[-0.3px] text-grey-70">
                    {format(new Date(props.evidence.activityDate), "d MMM yyyy")}
                  </div>
                  <div className="h-[1px] w-full bg-grey-30 mt-4" />
                </div>
              )}
              {props.evidence.activity && (
                <div className="mt-[20px]">
                  <div className="text-acc-13px font-bold leading-default tracking-[-0.1px] text-grey-90 mb-[4px]">
                    {"CPD Activity"}
                  </div>
                  <div className="text-acc-17px font-acc-normal leading-default tracking-[-0.3px] text-grey-70">
                    {props.evidence.activity.type}
                  </div>
                  <div className="h-[1px] w-full bg-grey-30 mt-4" />
                </div>
              )}
              {props.evidence.numberOfHours && (
                <div className="mt-[20px]">
                  <div className="text-acc-13px font-bold leading-default tracking-[-0.1px] text-grey-90 mb-[4px]">
                    {"Number of hours"}
                  </div>
                  <div className="text-acc-17px font-acc-normal leading-default tracking-[-0.3px] text-grey-70">
                    {StringUtils.convertToHoursMinutes(props.evidence.numberOfHours)}
                  </div>
                  <div className="h-[1px] w-full bg-grey-30 mt-4" />
                </div>
              )}
              {props.evidence.standardTags && props.evidence.standardTags.length > 0 && (
                <div className="mt-[20px]">
                  <div className="text-acc-13px font-bold leading-default tracking-[-0.1px] text-grey-90 mb-[4px]">
                    {"National Occupational Standards"}
                  </div>
                  <div className="text-acc-17px font-acc-normal leading-default tracking-[-0.3px] text-grey-70">
                    {props.evidence.standardTags
                      .map((tag: EvidenceStandardTag) => tag.tag)
                      .sort()
                      .join(", ")}
                  </div>
                  <div className="h-[1px] w-full bg-grey-30 mt-4" />
                </div>
              )}
              {props.evidence.customTags && props.evidence.customTags.length > 0 && (
                <div className="mt-[20px]">
                  <div className="text-acc-13px font-bold leading-default tracking-[-0.1px] text-grey-90 mb-[4px]">
                    {"Custom tags"}
                  </div>
                  <div className="text-acc-17px font-acc-normal leading-default tracking-[-0.3px] text-grey-70">
                    {props.evidence.customTags
                      .map((tag: EvidenceStandardTag) => tag.tag)
                      .sort()
                      .join(", ")}
                  </div>
                  <div className="h-[1px] w-full bg-grey-30 mt-4" />
                </div>
              )}
              {props.evidence.attachments && props.evidence.attachments.length > 0 && (
                <div className="mt-[20px]">
                  <div className="text-acc-13px font-bold leading-default tracking-[-0.1px] text-grey-90 mb-[4px]">
                    {"Attachments"}
                  </div>
                  <div className="text-acc-17px font-acc-500 leading-default tracking-[-0.3px] text-grey-70">
                    {props.evidence.attachments.map((attachment: EvidenceAttachment) => {
                      const disabled = viewingAttachment && viewingAttachmentFilename !== attachment.name;
                      const viewing = viewingAttachment && viewingAttachmentFilename === attachment.name;

                      return (
                        <div
                          key={attachment.name}
                          id={attachment.name}
                          className={`flex flex-row items-center cursor-pointer ${viewing && "cursor-wait"} ${
                            disabled ? "cursor-not-allowed" : ""
                          }`}
                          onClick={() => !(disabled || viewing) && viewAttachment(attachment)}
                        >
                          <div className="text-[17px] font-acc-500 leading-[1.53] tracking-[-0.3px] text-cta-blue underline">
                            {attachment.name}
                          </div>
                          {viewing && (
                            <div className="ml-[4px] w-[26px] h-[26px] flex items-center justify-center">
                              <IonSpinner className="w-[14px] h-[14px] text-cta-blue" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="h-[1px] w-full bg-grey-30 mt-4" />
                </div>
              )}
              {props.evidence.activityDescription && (
                <div className="mt-[20px]">
                  <div className="text-acc-13px font-bold leading-default tracking-[-0.1px] text-grey-90 mb-[4px]">
                    {"Activity description"}
                  </div>
                  <div className="text-acc-17px font-acc-normal leading-default tracking-[-0.3px] text-grey-70">
                    {props.evidence.activityDescription}
                  </div>
                  <div className="h-[1px] w-full bg-grey-30 mt-4" />
                </div>
              )}
              {props.evidence.reflections && (
                <div className="mt-[20px]">
                  <div className="text-acc-13px font-bold leading-default tracking-[-0.1px] text-grey-90 mb-[4px]">
                    {"Reflection"}
                  </div>
                  <div
                    className="text-acc-17px font-acc-normal leading-default tracking-[-0.3px] text-grey-70"
                    dangerouslySetInnerHTML={sanitizeData(props.evidence.reflections)}
                  />
                  <div className="h-[1px] w-full bg-grey-30 mt-4" />
                </div>
              )}
              {props.evidence.benefits && (
                <div className="mt-[20px]">
                  <div className="text-acc-13px font-bold leading-default tracking-[-0.1px] text-grey-90 mb-[4px]">
                    {"Benefits"}
                  </div>
                  <div
                    className="text-acc-17px font-acc-normal leading-default tracking-[-0.3px] text-grey-70"
                    dangerouslySetInnerHTML={sanitizeData(props.evidence.benefits)}
                  />
                  <div className="h-[1px] w-full bg-grey-30 mt-4" />
                </div>
              )}
              {props.evidence.objectives && (
                <div className="mt-[20px]">
                  <div className="text-acc-13px font-bold leading-default tracking-[-0.1px] text-grey-90 mb-[4px]">
                    {"Learning / Exercise Objectives"}
                  </div>
                  <div
                    className="text-acc-17px font-acc-normal leading-default tracking-[-0.3px] text-grey-70"
                    dangerouslySetInnerHTML={sanitizeData(props.evidence.objectives)}
                  />
                  <div className="h-[1px] w-full bg-grey-30 mt-4" />
                </div>
              )}
              <div className="mt-[20px]">
                <div className="text-acc-13px font-bold leading-default tracking-[-0.1px] text-grey-90 mb-[4px]">
                  {"Included in audit documents"}
                </div>
                <div className="text-acc-17px font-acc-normal leading-default tracking-[-0.3px] text-grey-70">
                  {props.evidence.addToHCPCAudit ? "Yes" : "No"}
                </div>
              </div>
            </>
          )}
          {evidenceJSON && (
            <LegacyEvidenceFields
              legacyParts={legacyParts}
              evidenceJSON={evidenceJSON}
              getLegacyEvidencePartType={getLegacyEvidencePartType}
              renderLegacyEvidencePart={renderLegacyEvidencePart}
            />
          )}
        </>
      ) : (
        <div>
          <div className="text-[28px] font-bold leading-[1.21] tracking-[-0.4px] text-grey-90">
            {"This piece of evidence doesn't exist"}
          </div>
          <div className="mt-[20px]">
            <div className="text-acc-17px font-acc-normal leading-default tracking-[-0.3px] text-grey-70">
              {
                "This piece of evidence might have been deleted recently, try going back and selecting another piece of evidence."
              }
              <br />
              <br />
              {"If you think this is a mistake, please "}
              {props.supportEmailInfo ? (
                <a
                  className="text-acc-17px font-acc-normal leading-default tracking-[-0.3px] text-cta-blue underline"
                  href={`mailto:apps@class.co.uk?subject=${props.supportEmailInfo.subject}&body=${props.supportEmailInfo.body}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {"contact support"}
                </a>
              ) : (
                <a
                  className="text-acc-17px font-acc-normal leading-default tracking-[-0.3px] text-cta-blue underline"
                  href="mailto:apps@class.co.uk?subject=ParaFolio support"
                  target="_blank"
                  rel="noreferrer"
                >
                  {"contact support"}
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvidenceDisplay;
